import React, { useEffect, useMemo, useState } from 'react';
import { ReactComponent as CampaignIcon } from '../../icons/campaigns.svg';
import {
  formatPromotionShortDate,
  promotionAudienceSummary,
  promotionChannelLabel,
  promotionDraftRows,
  promotionEndedRows,
  promotionLiveRows,
  type PromotionChannel,
  type PromotionSummary,
} from './promotionTypes';

type PromotionTab = 'live' | 'drafts' | 'ended';

interface PromotionsListProps {
  promotions: PromotionSummary[];
  sourceLabels: Record<string, string>;
  loading: boolean;
  onCreate: () => void;
  onOpen: (promotion: PromotionSummary) => void;
}

const channelSentLabel = (channel: PromotionChannel, sent: number) =>
  `${channel === 'whatsapp' ? 'WhatsApp' : 'Briefing'} ${sent} sent`;

const PromotionStats: React.FC<{ promotion: PromotionSummary }> = ({
  promotion,
}) => {
  const interested = Object.values(promotion.channelStats).reduce(
    (sum, counts) => sum + counts.interested,
    0,
  );
  const declined = Object.values(promotion.channelStats).reduce(
    (sum, counts) => sum + counts.declined,
    0,
  );
  const stats = [
    ['Audience', promotion.counts.audience],
    ['Sent', promotion.counts.sent],
    ['Interested', interested],
    ['Declined', declined],
    ['Clicked', promotion.counts.clicked],
  ];

  return (
    <>
      <dl className='promotion-row__stats'>
        {stats.map(([label, value]) => (
          <div key={label}>
            <dt>{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
      <p className='promotion-row__channels'>
        {(promotion.channels ?? ['whatsapp']).map((channel, index) => (
          <React.Fragment key={channel}>
            {index > 0 && <span aria-hidden='true'> · </span>}
            {channelSentLabel(
              channel,
              promotion.channelStats[channel]?.sent ?? 0,
            )}
          </React.Fragment>
        ))}
      </p>
    </>
  );
};

const PromotionsList: React.FC<PromotionsListProps> = ({
  promotions,
  sourceLabels,
  loading,
  onCreate,
  onOpen,
}) => {
  const rows = useMemo(
    () => ({
      live: promotionLiveRows(promotions).sort(
        (left, right) =>
          Number(Boolean(right.codeDefined)) -
          Number(Boolean(left.codeDefined)),
      ),
      drafts: promotionDraftRows(promotions).sort(
        (left, right) =>
          Number(Boolean(right.codeDefined)) -
          Number(Boolean(left.codeDefined)),
      ),
      ended: promotionEndedRows(promotions).sort(
        (left, right) =>
          Number(Boolean(right.codeDefined)) -
          Number(Boolean(left.codeDefined)),
      ),
    }),
    [promotions],
  );
  const [activeTab, setActiveTab] = useState<PromotionTab | null>(null);

  useEffect(() => {
    if (activeTab === null && !loading) {
      setActiveTab(rows.live.length > 0 ? 'live' : 'drafts');
    }
  }, [activeTab, loading, rows.live.length]);

  const tabs: { id: PromotionTab; label: string }[] = [
    { id: 'live', label: 'Live' },
    { id: 'drafts', label: 'Drafts' },
    { id: 'ended', label: 'Closed & paused' },
  ];
  const selectedTab = activeTab ?? 'live';

  const emptyState = {
    live: {
      heading: 'No live promotions',
      message: 'When a promotion is live, its progress will appear here.',
    },
    drafts: {
      heading: 'No drafts yet',
      message: 'Create a promotion to get started.',
    },
    ended: {
      heading: 'No closed or paused promotions',
      message: 'Paused, closed and finished promotions will appear here.',
    },
  }[selectedTab];

  return (
    <section className='promotion-list'>
      <header className='promotion-list__header'>
        <div>
          <h3>Promotions</h3>
          <p className='launch-description'>
            Reach your audience across WhatsApp and Daily Briefing.
          </p>
        </div>
        <button
          className='launch-btn launch-btn--primary'
          type='button'
          onClick={onCreate}
        >
          Create promotion
        </button>
      </header>
      <div className='promotion-tabs' role='tablist' aria-label='Promotions'>
        {tabs.map(({ id, label }) => (
          <button
            key={id}
            type='button'
            role='tab'
            aria-selected={selectedTab === id}
            className={`promotion-tab${selectedTab === id ? ' is-active' : ''}`}
            onClick={() => setActiveTab(id)}
          >
            {label} <span>{rows[id].length}</span>
          </button>
        ))}
      </div>
      {loading ? (
        <p className='launch-hint'>Loading promotions…</p>
      ) : rows[selectedTab].length === 0 ? (
        <div className='promotion-empty-state' role='status'>
          <CampaignIcon aria-hidden='true' />
          <h4>{emptyState.heading}</h4>
          <p>{emptyState.message}</p>
        </div>
      ) : (
        <div
          className='promotion-list__rows'
          role='tabpanel'
          aria-label={`${tabs.find((tab) => tab.id === selectedTab)?.label} promotions`}
        >
          {rows[selectedTab].map((promotion) => (
            <button
              key={promotion.slug}
              type='button'
              className={`promotion-row promotion-row--${selectedTab}`}
              onClick={() => onOpen(promotion)}
            >
              <span className='promotion-row__top'>
                <strong>{promotion.name}</strong>
                {selectedTab === 'live' &&
                  (promotion.status === 'launching' ? (
                    <span className='launch-status launch-status--launching'>
                      Launching…
                    </span>
                  ) : (
                    <span className='launch-status launch-status--live'>
                      Live
                    </span>
                  ))}
                {selectedTab === 'drafts' && (
                  <span className='launch-status launch-status--draft'>
                    Draft
                  </span>
                )}
                {selectedTab === 'ended' &&
                  (promotion.status === 'paused' ? (
                    <span className='launch-status launch-status--paused'>
                      Paused
                    </span>
                  ) : (
                    <span className='launch-status launch-status--closed'>
                      {promotion.status === 'closed' ? 'Closed' : 'Ended'}
                    </span>
                  ))}
              </span>
              <span className='promotion-row__audience'>
                {promotionAudienceSummary(
                  promotion.audience,
                  promotion.holdoutPercent,
                  sourceLabels,
                )}
              </span>
              <span className='promotion-row__dates'>
                {formatPromotionShortDate(promotion.startDate)} –{' '}
                {formatPromotionShortDate(promotion.endDate)}
              </span>
              {selectedTab === 'drafts' ? (
                <span className='promotion-row__chips'>
                  {(promotion.channels ?? ['whatsapp']).map((channel) => (
                    <span className='promotion-channel-chip' key={channel}>
                      {promotionChannelLabel(channel)}
                    </span>
                  ))}
                </span>
              ) : (
                <PromotionStats promotion={promotion} />
              )}
            </button>
          ))}
        </div>
      )}
    </section>
  );
};

export default PromotionsList;
